import type { TFunction } from 'i18next';
import { cloneDeep } from 'lodash';

import { initCommandApiToFormFields, initCommandFormFieldsToApi } from '../command-args-normal';
import type { MaasTaskStepFormValues, MaasTaskTemplateStep } from './types';

/** 将历史 taskTemplate 步骤中的 timeout/retryCount 归并到 command 内 */
export function normalizeTaskTemplateStep(step: MaasTaskTemplateStep): MaasTaskTemplateStep {
  const legacy = step as MaasTaskTemplateStep & { timeout?: unknown; retryCount?: number };
  const command = { ...(step.command || { commands: [] }) };
  const legacyTimeout = legacy.timeout ?? command.timeout;
  const normalizedTimeout = parseTimeoutSeconds(legacyTimeout);
  if (normalizedTimeout != null) {
    command.timeout = normalizedTimeout;
  } else {
    delete command.timeout;
  }
  if (legacy.retryCount != null && command.retryCount == null) {
    command.retryCount = legacy.retryCount;
  }
  const { timeout: _timeout, retryCount: _retryCount, ...rest } = legacy;
  return { ...rest, command };
}

/** 解析表单 taskTemplate 字段（JSON 字符串或数组） */
export function parseTaskTemplateField(raw: unknown): MaasTaskTemplateStep[] {
  if (raw == null || raw === '') return [];
  let steps: MaasTaskTemplateStep[] = [];
  if (Array.isArray(raw)) {
    steps = cloneDeep(raw) as MaasTaskTemplateStep[];
  } else if (typeof raw === 'string') {
    try {
      const parsed = JSON.parse(raw);
      steps = Array.isArray(parsed) ? (cloneDeep(parsed) as MaasTaskTemplateStep[]) : [];
    } catch {
      return [];
    }
  }
  return steps.map(normalizeTaskTemplateStep);
}

/** 超时值（数字或如 300s 的历史字符串）→ 秒数 */
export function parseTimeoutSeconds(raw: unknown): number | undefined {
  if (raw == null || raw === '') return undefined;
  if (typeof raw === 'number' && Number.isFinite(raw) && raw > 0) return Math.floor(raw);
  const text = String(raw).trim();
  if (!text) return undefined;
  const match = text.match(/^(\d+(?:\.\d+)?)\s*s?$/i);
  if (!match) return undefined;
  const value = Number(match[1]);
  return Number.isFinite(value) && value > 0 ? Math.floor(value) : undefined;
}

/** 秒数 → API timeout 数字 */
export function formatTimeoutSeconds(seconds: number | undefined): number | undefined {
  if (seconds == null || !Number.isFinite(seconds) || seconds <= 0) return undefined;
  return Math.floor(seconds);
}

/** 规范化 dependsOn：去空白、去空串、去重，可选按允许列表过滤 */
export function normalizeDependsOn(raw: unknown, allowedNames?: string[]): string[] {
  const seen = new Set<string>();
  const names: string[] = [];
  for (const item of Array.isArray(raw) ? raw : []) {
    const name = String(item ?? '').trim();
    if (!name || seen.has(name)) continue;
    seen.add(name);
    names.push(name);
  }
  if (!allowedNames) return names;
  const allowed = new Set(allowedNames);
  return names.filter((name) => allowed.has(name));
}

/** 弹窗表单 → API 任务步骤 */
export function taskStepFormToApi(values: MaasTaskStepFormValues): MaasTaskTemplateStep {
  const commandBase = initCommandFormFieldsToApi(values.command) ?? { commands: [] };
  const timeout = formatTimeoutSeconds(values.timeoutSeconds);
  const command = {
    ...commandBase,
    ...(timeout != null ? { timeout } : {}),
    ...(values.retryCount != null ? { retryCount: values.retryCount } : {}),
  };
  const groups = (values.groups ?? []).map((g) => String(g).trim()).filter(Boolean);
  return {
    name: String(values.name ?? '').trim() || 'step-0',
    role: values.role ?? 'All',
    groups,
    command,
    runStrategy: values.runStrategy ?? 'Parallel',
    failurePolicy: values.failurePolicy ?? 'Terminate',
    async: values.async === true,
    dependsOn: normalizeDependsOn(values.dependsOn),
  };
}

/** API 任务步骤 → 弹窗表单 */
export function taskStepApiToForm(step: MaasTaskTemplateStep, options?: { allowedDependsOn?: string[] }): MaasTaskStepFormValues {
  const normalized = normalizeTaskTemplateStep(step);
  const legacy = step as MaasTaskTemplateStep & { timeout?: unknown; retryCount?: number };
  const timeoutRaw = normalized.command?.timeout ?? legacy.timeout;
  const retryCount = normalized.command?.retryCount ?? legacy.retryCount ?? 0;
  return {
    name: normalized.name,
    role: normalized.role ?? 'All',
    groups: normalized.groups ? [...normalized.groups] : undefined,
    runStrategy: normalized.runStrategy ?? 'Parallel',
    failurePolicy: normalized.failurePolicy ?? 'Terminate',
    async: normalized.async === true,
    dependsOn: normalizeDependsOn(normalized.dependsOn, options?.allowedDependsOn),
    timeoutSeconds: parseTimeoutSeconds(timeoutRaw),
    retryCount,
    command: initCommandApiToFormFields(normalized.command),
  };
}

/** 自动生成步骤名称（index 从 0 起，展示为 step-1、step-2…） */
export function buildDefaultTaskStepName(index: number): string {
  return `step-${index + 1}`;
}

/** 读取任务步骤超时（优先 command.timeout，单位：秒） */
export function resolveTaskStepTimeout(step: MaasTaskTemplateStep): number | undefined {
  const normalized = normalizeTaskTemplateStep(step);
  return normalized.command?.timeout;
}

/** 读取任务步骤重试次数（优先 command.retryCount） */
export function resolveTaskStepRetryCount(step: MaasTaskTemplateStep): number {
  const normalized = normalizeTaskTemplateStep(step);
  return normalized.command?.retryCount ?? 0;
}

/** 只读「查看命令」弹窗标题：启动命令：组件名·步骤名 */
export function formatTaskStepStartupCommandModalTitle(t: TFunction, componentLabel: string, stepName: string): string {
  return `${t('components:startup.command')}：${componentLabel}·${stepName}`;
}
